import { useRef } from 'react';
import ParticleNetwork from './ParticleNetwork';
import { useScrollY, NoteKey } from '../hooks';

interface HeroProps {
  tagline: string;
  slogan: string;
  ctaText: string;
  contactText: string;
  scrollText: string;
  mouseX: number;
  mouseY: number;
  onPlayNote: (note: NoteKey) => void;
  onCtaClick?: () => void;
  onContactClick?: () => void;
}

export default function Hero({
  tagline,
  slogan,
  ctaText,
  contactText,
  scrollText,
  mouseX,
  mouseY,
  onPlayNote,
  onCtaClick,
  onContactClick,
}: HeroProps) {
  const heroRef = useRef<HTMLElement>(null);
  const scrollY = useScrollY();

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        background: `radial-gradient(circle at ${mouseX * 100}% ${mouseY * 100}%, rgba(0, 212, 255, 0.15) 0%, transparent 50%), linear-gradient(135deg, var(--navy-deep) 0%, var(--navy-mid) 50%, #0d2137 100%)`,
      }}
    >
      <ParticleNetwork mouseX={mouseX} mouseY={mouseY} />

      {/* Animated grid lines */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 212, 255, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 212, 255, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '100px 100px',
            transform: `translateY(${scrollY * 0.1}px)`,
          }}
        />
      </div>

      {/* Hero content */}
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        <div className="animate-fade-up opacity-0" style={{ animationFillMode: 'forwards' }}>
          <h1 className="text-6xl md:text-8xl font-bold mb-6 tracking-tight">
            <span className="gradient-text">{tagline}</span>
          </h1>
        </div>

        <div
          className="animate-fade-up opacity-0 delay-200"
          style={{ animationFillMode: 'forwards' }}
        >
          <p className="text-2xl md:text-3xl text-gray-300 mb-8 font-light">{slogan}</p>
        </div>

        <div
          className="animate-fade-up opacity-0 delay-300 flex flex-col sm:flex-row gap-4 justify-center"
          style={{ animationFillMode: 'forwards' }}
        >
          <button
            onMouseEnter={() => onPlayNote('E5')}
            onClick={() => {
              onPlayNote('G5');
              setTimeout(() => onPlayNote('C6'), 100);
              onCtaClick?.();
            }}
            className="rgb-border-hover px-10 py-4 rounded-full bg-gradient-to-r from-[var(--cyan-electric)] to-cyan-400 text-[var(--navy-deep)] font-semibold text-lg hover:scale-105 transition-transform"
          >
            {ctaText} →
          </button>
          <button
            onMouseEnter={() => onPlayNote('D5')}
            onClick={() => onContactClick?.()}
            className="rgb-border-hover px-10 py-4 rounded-full border border-white/20 bg-[var(--navy-deep)] hover:bg-white/10 transition-colors text-lg text-white"
          >
            {contactText}
          </button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="text-sm text-gray-400">{scrollText}</span>
        <div className="animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </div>
    </section>
  );
}
